import '../styles/TrackShipmentLookup.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const STAGES = ['Picked up', 'In transit', 'Customs', 'Out for delivery', 'Delivered'];

const EMPTY_FIELDS = [
  'Waybill number',
  'Origin',
  'Destination',
  'Weight',
  'Dimensions',
  'Estimated delivery',
];

const PLACEHOLDER_WAYBILL = 'FFD-0000-0000';

/* ---------- gauge geometry ---------- */
const GAUGE_SIZE = 240;
const GAUGE_CX = 120;
const GAUGE_CY = 120;
const GAUGE_R = 94;
const GAUGE_SWEEP = 270;
const GAUGE_START = 135;

function polarPoint(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startDeg, endDeg) {
  const start = polarPoint(cx, cy, r, endDeg);
  const end = polarPoint(cx, cy, r, startDeg);
  const largeArc = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

const GAUGE_TRACK_PATH = describeArc(GAUGE_CX, GAUGE_CY, GAUGE_R, GAUGE_START, GAUGE_START + GAUGE_SWEEP);
const GAUGE_LEN = (2 * Math.PI * GAUGE_R * GAUGE_SWEEP) / 360;

function stageIndex(stage) {
  const idx = STAGES.indexOf(stage);
  return idx < 0 ? 0 : idx;
}

/* ---------- tabular count-up, runs once per record ---------- */
function CountUp({ value, animate }) {
  const match = String(value ?? '').match(/-?\d[\d,]*(?:\.\d+)?/);
  const target = match ? parseFloat(match[0].replace(/,/g, '')) : null;
  const prefix = match ? String(value).slice(0, match.index) : '';
  const suffix = match ? String(value).slice(match.index + match[0].length) : '';
  const decimals = match && match[0].includes('.') ? match[0].split('.')[1].length : 0;

  const [display, setDisplay] = useState(target === null ? value : prefix + target.toFixed(decimals) + suffix);
  const frameRef = useRef(0);

  useEffect(() => {
    if (target === null || !animate) {
      setDisplay(value);
      return undefined;
    }
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDisplay(value);
      return undefined;
    }
    const duration = 600;
    const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = target * eased;
      setDisplay(prefix + current.toFixed(decimals) + suffix);
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(value);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, animate]);

  return <>{display}</>;
}

function TrackShipmentLookup({ onShipmentResolved }) {
  const { data, loading, error } = useProjectCollection('consignment_shipments');

  const [waybillInput, setWaybillInput] = useState('');
  const [resolvedShipmentId, setResolvedShipmentId] = useState('');
  const [lookupStatus, setLookupStatus] = useState('idle'); // idle | resolving | resolved | unresolved | failed
  const [lookupError, setLookupError] = useState('');
  const [lastKnownStage, setLastKnownStage] = useState('');

  const [revealed, setRevealed] = useState(false);
  const [routeDrawn, setRouteDrawn] = useState(false);

  const shipments = Array.isArray(data) ? data : [];
  const hasUnresolvedId = resolvedShipmentId !== '';
  const shipment = hasUnresolvedId
    ? shipments.find((s) => s.id === resolvedShipmentId) || null
    : null;

  const activeStage = shipment ? shipment.stage : lastKnownStage;
  const currentIndex = stageIndex(activeStage);
  const isDelivered = activeStage === 'Delivered';

  /* reveal choreography: needle sweep + count-up + route draw */
  useEffect(() => {
    setRevealed(false);
    setRouteDrawn(false);
    if (lookupStatus !== 'resolved' || !shipment) return undefined;
    const t1 = window.setTimeout(() => setRevealed(true), 40);
    const t2 = window.setTimeout(() => setRouteDrawn(true), 260);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [lookupStatus, shipment]);

  /* when the backend resolves or fails, hold the gauge at its last known value */
  useEffect(() => {
    if (!hasUnresolvedId) return;
    if (loading && lookupStatus === 'resolving') return;
    if (error) {
      setLookupStatus('failed');
      setLookupError(
        `The registry could not be read (${error.message || 'connection failed'}). The gauge is holding its last known stage. Retry to resolve this waybill.`
      );
      return;
    }
    if (shipment) {
      setLookupStatus('resolved');
      setLookupError('');
      setLastKnownStage(shipment.stage);
    } else if (lookupStatus === 'resolving') {
      setLookupStatus('unresolved');
      setLookupError(
        `No consignment matches waybill ${resolvedShipmentId}. Check the waybill number against the sender's document and retry, or clear the field to look up a different shipment.`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, shipment, hasUnresolvedId]);

  const needleAngle = useMemo(() => {
    const shown = revealed && shipment ? currentIndex : currentIndex;
    return GAUGE_START + (GAUGE_SWEEP * shown) / (STAGES.length - 1);
  }, [currentIndex, revealed, shipment]);

  const progressOffset = revealed
    ? GAUGE_LEN - (GAUGE_LEN * currentIndex) / (STAGES.length - 1)
    : GAUGE_LEN;

  function updateResolvedShipmentId(id) {
    setResolvedShipmentId(id);
    if (typeof onShipmentResolved === 'function') {
      onShipmentResolved(id);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    const raw = waybillInput.trim();
    const normalized = raw.toUpperCase().replace(/\s+/g, '');

    if (normalized === '') {
      setLookupError('Enter a waybill number before submitting the lookup. The number is printed on the consignment document.');
      setLookupStatus('idle');
      updateResolvedShipmentId('');
      return;
    }
    if (!/^FFD-\d{4}-\d{4}$/.test(normalized)) {
      setLookupError(`"${raw}" is not a valid waybill number. Use the FFD-NNNN-NNNN format shown on the consignment document, then retry.`);
      setLookupStatus('idle');
      updateResolvedShipmentId('');
      return;
    }

    updateResolvedShipmentId(normalized);
    setLookupError('');
    setLookupStatus('resolving');
  }

  function handleRetry() {
    if (!resolvedShipmentId) return;
    setLookupError('');
    setLookupStatus('resolving');
    setResolvedShipmentId((id) => id);
  }

  function handleReset() {
    setWaybillInput('');
    updateResolvedShipmentId('');
    setLookupStatus('idle');
    setLookupError('');
    setLastKnownStage('');
    setRevealed(false);
    setRouteDrawn(false);
  }

  const showConsole = hasUnresolvedId && lookupStatus !== 'idle';

  return (
    <section className="tsl-section" aria-labelledby="tsl-title">
      <div className="tsl-inner">
        <header className="tsl-head">
          <span className="tsl-eyebrow">
            <span className="tsl-eyebrow-pip" aria-hidden="true" />
            Fast Forward Deliver Services · Consignment tracking
          </span>
          <h1 className="tsl-title" id="tsl-title">
            Track a consignment
          </h1>
          <p className="tsl-lede">
            Enter the waybill number from your consignment document to read the shipment's stage, route,
            weight and estimated delivery. The record stays attached to the sender and recipient it was
            issued to.
          </p>
        </header>

        {/* ---------- WaybillLookupRow ---------- */}
        <form className="tsl-lookup" onSubmit={handleSubmit} noValidate>
          <div className="tsl-lookup-row">
            <div className="tsl-field">
              <label className="tsl-label" htmlFor="tsl-waybill">
                Waybill number
              </label>
              <input
                id="tsl-waybill"
                className={'tsl-input' + (lookupError ? ' is-invalid' : '')}
                type="text"
                name="waybill"
                inputMode="text"
                autoComplete="off"
                spellCheck="false"
                placeholder={PLACEHOLDER_WAYBILL}
                value={waybillInput}
                onChange={(e) => setWaybillInput(e.target.value)}
                aria-invalid={lookupError ? 'true' : 'false'}
                aria-describedby="tsl-baseline"
              />
            </div>
            <div className="tsl-actions">
              <button
                type="submit"
                className="tsl-btn tsl-btn--primary"
                disabled={lookupStatus === 'resolving'}
              >
                <span className="tsl-btn__pip" aria-hidden="true" />
                {lookupStatus === 'resolving' ? 'Resolving' : 'Look up waybill'}
              </button>
            </div>
          </div>

          <div
            className={'tsl-baseline' + (lookupError ? ' tsl-baseline--error' : '')}
            id="tsl-baseline"
            role={lookupError ? 'alert' : undefined}
          >
            <span className="tsl-baseline__tag">{lookupError ? 'Lookup' : 'Format'}</span>
            <span className="tsl-baseline__text">
              {lookupError ? (
                <>
                  {lookupError}
                  {resolvedShipmentId ? (
                    <>
                      {' '}
                      Waybill <code>{resolvedShipmentId}</code> is held in the field below.
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  Waybills are issued in the format <code>FFD-NNNN-NNNN</code>. Read it from the sender's
                  consignment document — it is the serial for the tracked object, its route and its timestamps.
                </>
              )}
            </span>
          </div>
        </form>

        {/* ---------- ResolvedShipmentConsole ---------- */}
        {showConsole ? (
          <div className="tsl-console" aria-live="polite">
            <div className="tsl-console__bar">
              <span className="tsl-console__ref">
                {shipment ? `Waybill ${shipment.waybillNumber}` : `Waybill ${resolvedShipmentId}`}
              </span>
              <span className="tsl-console__meta">
                {lookupStatus === 'resolving' ? 'Resolving record…' : null}
                {lookupStatus === 'unresolved' ? 'No matching record' : null}
                {lookupStatus === 'failed' ? 'Registry read failed' : null}
                {lookupStatus === 'resolved' && shipment
                  ? `Issued ${shipment.createdAt} · Custody: ${lastKnownStage || shipment.stage}`
                  : null}
              </span>
            </div>

            <div className="tsl-console__body">
              <div className="tsl-gauge-col">
                <div className="tsl-gauge">
                  <svg viewBox="0 0 240 240" role="img" aria-label={`Status gauge: ${activeStage}`}>
                    <path className="tsl-gauge__track" d={GAUGE_TRACK_PATH} />
                    <path
                      className={'tsl-gauge__progress' + (isDelivered ? ' tsl-gauge__progress--done' : '')}
                      d={GAUGE_TRACK_PATH}
                      style={{ strokeDasharray: GAUGE_LEN, strokeDashoffset: progressOffset }}
                    />
                    <g>
                      {STAGES.map((stage, i) => {
                        const a = GAUGE_START + (GAUGE_SWEEP * i) / (STAGES.length - 1);
                        const inner = polarPoint(GAUGE_CX, GAUGE_CY, GAUGE_R - 14, a);
                        const outer = polarPoint(GAUGE_CX, GAUGE_CY, GAUGE_R - 4, a);
                        const cls =
                          'tsl-gauge__tick' +
                          (i === currentIndex ? ' tsl-gauge__tick--active' : '') +
                          (i < currentIndex ? ' tsl-gauge__tick--done' : '');
                        return (
                          <line
                            key={stage}
                            className={cls}
                            x1={inner.x}
                            y1={inner.y}
                            x2={outer.x}
                            y2={outer.y}
                          />
                        );
                      })}
                    </g>
                    <text className="tsl-gauge__readout-label" x={GAUGE_CX} y={GAUGE_CY + 44} textAnchor="middle">
                      ETA UTC
                    </text>
                    <text className="tsl-gauge__readout" x={GAUGE_CX} y={GAUGE_CY + 62} textAnchor="middle">
                      {shipment ? <CountUp value={shipment.estimatedDelivery} animate={revealed} /> : '—'}
                    </text>
                    <line
                      className="tsl-gauge__needle"
                      x1={GAUGE_CX}
                      y1={GAUGE_CY}
                      x2={GAUGE_CX}
                      y2={GAUGE_CY - (GAUGE_R - 20)}
                      style={{ transform: `rotate(${needleAngle + 90}deg)` }}
                    />
                    <circle className="tsl-gauge__hub" cx={GAUGE_CX} cy={GAUGE_CY} r="5" />
                    <circle className="tsl-gauge__hub-ring" cx={GAUGE_CX} cy={GAUGE_CY} r="10" />
                    <text className="tsl-gauge__scale" x={30} y={GAUGE_CY + 6} textAnchor="middle">
                      01
                    </text>
                    <text className="tsl-gauge__scale" x={210} y={GAUGE_CY + 6} textAnchor="middle">
                      05
                    </text>
                  </svg>
                </div>

                <div className="tsl-stage">
                  <h3 className={'tsl-stage__value' + (isDelivered ? ' tsl-stage__value--done' : '')}>
                    {activeStage}
                  </h3>
                  <span className="tsl-stage__sub">
                    Stage {currentIndex + 1} of {STAGES.length}
                    {lookupStatus === 'resolving' ? ' · Holding last known' : ''}
                  </span>
                </div>

                <ul className="tsl-ladder">
                  {STAGES.map((stage, i) => {
                    const cls =
                      'tsl-ladder__item' +
                      (i === currentIndex ? ' is-current' : '') +
                      (i < currentIndex ? ' is-past' : '') +
                      (stage === 'Delivered' && isDelivered ? ' is-done' : '');
                    return (
                      <li key={stage} className={cls}>
                        <span className="tsl-ladder__dot" aria-hidden="true" />
                        {stage}
                      </li>
                    );
                  })}
                </ul>

                {lookupStatus === 'resolving' ? (
                  <span className="tsl-hold">
                    <span className="tsl-hold__bar" aria-hidden="true" />
                    Resolving record
                  </span>
                ) : null}
              </div>

              <div className="tsl-data-col">
                {shipment ? (
                  <>
                    <div className="tsl-route">
                      <svg viewBox="0 0 520 96" role="img" aria-label={`Route from ${shipment.origin} to ${shipment.destination}`}>
                        <path
                          className="tsl-route__ghost"
                          d="M 60 62 C 170 18, 350 18, 460 62"
                        />
                        <path
                          className={'tsl-route__line' + (routeDrawn ? ' is-drawn' : '')}
                          style={{ '--tsl-route-len': 470 }}
                          d="M 60 62 C 170 18, 350 18, 460 62"
                        />
                        <circle className="tsl-route__pin" cx="60" cy="62" r="8" />
                        <circle className="tsl-route__pin-core" cx="60" cy="62" r="2.5" />
                        <circle
                          className={'tsl-route__pin tsl-route__pin--dest' + (isDelivered ? ' tsl-route__pin--done' : '')}
                          cx="460"
                          cy="62"
                          r="8"
                        />
                        <circle
                          className={'tsl-route__pin-core' + (isDelivered ? '' : '')}
                          cx="460"
                          cy="62"
                          r="2.5"
                          style={isDelivered ? { fill: 'var(--secondary)' } : undefined}
                        />
                        <text className="tsl-route__place" x="60" y="30" textAnchor="middle">
                          {shipment.origin}
                        </text>
                        <text className="tsl-route__place-sub" x="60" y="44" textAnchor="middle">
                          ORIGIN
                        </text>
                        <text className="tsl-route__place" x="460" y="30" textAnchor="middle">
                          {shipment.destination}
                        </text>
                        <text className="tsl-route__place-sub" x="460" y="44" textAnchor="middle">
                          DESTINATION
                        </text>
                      </svg>
                      <div className="tsl-route-caption">
                        <span>Route · {shipment.origin} → {shipment.destination}</span>
                        <span>Custody · {STAGES.length} stages</span>
                      </div>
                    </div>

                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Waybill</span>
                      <span className="tsl-datarow__value tsl-datarow__value--key">
                        {shipment.waybillNumber}
                      </span>
                    </div>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Origin</span>
                      <span className="tsl-datarow__value">{shipment.origin}</span>
                    </div>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Destination</span>
                      <span className="tsl-datarow__value">{shipment.destination}</span>
                    </div>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Weight</span>
                      <span className="tsl-datarow__value">
                        <CountUp value={shipment.weight} animate={revealed} />
                      </span>
                    </div>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Dimensions</span>
                      <span className="tsl-datarow__value">{shipment.dimensions}</span>
                    </div>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Est. delivery</span>
                      <span className="tsl-datarow__value">
                        <CountUp value={shipment.estimatedDelivery} animate={revealed} />
                      </span>
                    </div>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Contents</span>
                      <span className="tsl-datarow__value">{shipment.declaredContents}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Waybill</span>
                      <span className="tsl-datarow__value tsl-datarow__value--key">{resolvedShipmentId}</span>
                    </div>
                    <div className="tsl-datarow">
                      <span className="tsl-datarow__label">Record</span>
                      <span className="tsl-datarow__value">
                        {lookupStatus === 'resolving'
                          ? 'Reading the consignment registry…'
                          : lookupStatus === 'failed'
                            ? 'Registry read failed — gauge held at last known stage'
                            : 'No matching consignment in the registry'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="tsl-console__foot">
              {lookupStatus === 'resolved' ? (
                <button type="button" className="tsl-btn tsl-btn--ghost" onClick={handleReset}>
                  Look up another shipment
                </button>
              ) : (
                <button type="button" className="tsl-btn tsl-btn--ghost" onClick={handleRetry}>
                  <span className="tsl-btn__pip" aria-hidden="true" />
                  Retry resolve
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="tsl-placeholder">
            <h2 className="tsl-placeholder__title">No waybill resolved yet</h2>
            <p className="tsl-placeholder__note">
              This console is blank until a waybill resolves. When it does, the record opens here as an
              instrument reading: the stage gauge at the left, the route across the top of the data, and the
              measured values set in ruled tabular rows.
            </p>
            <ul className="tsl-fields-list">
              {EMPTY_FIELDS.map((field, i) => (
                <li key={field} className={i === 0 ? 'is-live' : ''}>
                  <span aria-hidden="true" />
                  {field}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

export default TrackShipmentLookup;